import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import * as Communication from "neo-games/Engine/Communication/index";

/**
 * Class reel represent reel symbols rotation.
 * @class ReelExtended
 * @extends Reel
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private _syncedStrip: Array<number>;

    private _syncedReelBg: SwitchElement;

    public isSyncedReel: boolean;

    //******************************************************************************************************************
    //  INITIALIZATION
    //******************************************************************************************************************

    public setSyncedStrip(strip: Array<number>, stripIndex: number): void {
        this._syncedStrip = strip;
        this.stripIndex = stripIndex;
    }

    public setSyncedReelBg(syncedReelBg: SwitchElement, customData: any): void {
        this._syncedReelBg = syncedReelBg;

        //show sync reel init state
        if (customData && customData["isSynced"]) {
            this._syncedReelBg.goToCase("INIT");
        }
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        let strip = this.strip;

        if (this._syncedStrip) {
            strip = this._syncedStrip;
            this._syncedStrip = null;
        }

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                symbolId =
                    strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + strip.length) %
                            strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + strip.length) %
                            strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.shift());
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            this.addChild(this.currentSymbols[i]);

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        let strip = this.strip;
        if (this.isSyncedReel) {
            strip = this._syncedStrip;
        }

        this.stripIndex = (this.stripIndex - 1 + strip.length) % strip.length;

        let symbolId = strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            strip.length) %
                            strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                symbolId =
                    strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            strip.length) %
                            strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  ADDITIONAL
    //******************************************************************************************************************

    public getStrip(): Array<number> {
        return this.strip;
    }

    public getStripIndex(): number {
        return this.stripIndex;
    }

    public setSyncedReelBgCase(caseType: string): void {
        if (this._syncedReelBg) {
            if (
                (caseType === "RESET" && this._syncedReelBg.getCurrentCase() === "EMPTY") ||
                (caseType === "FORCE" && this._syncedReelBg.getCurrentCase() === "END")
            ) {
                return;
            }

            if (caseType === "FORCE" && this._syncedReelBg.getCurrentCase() !== "EMPTY") {
                caseType = "END_FORCE";
            }

            this._syncedReelBg.goToCase(caseType);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/